import api from './api';
import {
  CalendarMappingRule,
  CreateMappingRuleData,
  UpdateMappingRuleData,
  MappingRuleFilters,
  MappingRuleApiResponse,
} from '@/types/mapping-rules';

/**
 * Calendar Mapping Rules API Service
 *
 * API client for calendar mapping rule management endpoints
 * Handles auto-distribution of post variations to target calendars
 */

export const mappingRulesService = {
  /**
   * List all mapping rules with optional filters
   */
  getMappingRules: async (filters?: MappingRuleFilters): Promise<CalendarMappingRule[]> => {
    const queryParams = new URLSearchParams();

    if (filters?.sourceCalendarId !== undefined) {
      queryParams.append('sourceCalendarId', filters.sourceCalendarId.toString());
    }
    if (filters?.targetCalendarId !== undefined) {
      queryParams.append('targetCalendarId', filters.targetCalendarId.toString());
    }
    if (filters?.variationTitle) {
      queryParams.append('variationTitle', filters.variationTitle);
    }

    const { data } = await api.get<MappingRuleApiResponse<CalendarMappingRule[]>>(
      `/api/calendar-mapping-rules${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
    return data.data;
  },

  /**
   * Get single mapping rule by ID
   */
  getMappingRule: async (id: number): Promise<CalendarMappingRule> => {
    const { data } = await api.get<MappingRuleApiResponse<CalendarMappingRule>>(
      `/api/calendar-mapping-rules/${id}`
    );
    return data.data;
  },

  /**
   * Get mapping rules for a specific calendar
   */
  getMappingRulesByCalendar: async (calendarId: number): Promise<CalendarMappingRule[]> => {
    const { data } = await api.get<MappingRuleApiResponse<CalendarMappingRule[]>>(
      `/api/calendar-mapping-rules/by-calendar/${calendarId}`
    );
    return data.data;
  },

  /**
   * Create new mapping rule
   */
  createMappingRule: async (ruleData: CreateMappingRuleData): Promise<CalendarMappingRule> => {
    const { data } = await api.post<MappingRuleApiResponse<CalendarMappingRule>>(
      '/api/calendar-mapping-rules',
      ruleData
    );
    return data.data;
  },

  /**
   * Update mapping rule
   */
  updateMappingRule: async (
    id: number,
    ruleData: UpdateMappingRuleData
  ): Promise<CalendarMappingRule> => {
    const { data } = await api.patch<MappingRuleApiResponse<CalendarMappingRule>>(
      `/api/calendar-mapping-rules/${id}`,
      ruleData
    );
    return data.data;
  },

  /**
   * Delete mapping rule
   */
  deleteMappingRule: async (id: number): Promise<void> => {
    await api.delete(`/api/calendar-mapping-rules/${id}`);
  },

  /**
   * Batch save rules for a calendar (replaces all existing rules)
   */
  saveRulesForCalendar: async (
    sourceCalendarId: number,
    rules: Array<{ targetCalendarId: number; variationTitle: string }>
  ): Promise<CalendarMappingRule[]> => {
    const { data } = await api.post<MappingRuleApiResponse<CalendarMappingRule[]>>(
      '/api/calendar-mapping-rules/batch',
      { sourceCalendarId, rules }
    );
    return data.data;
  },

  /**
   * Apply rules retroactively to existing posts
   */
  applyRulesRetroactive: async (
    sourceCalendarId: number,
    dateFrom?: Date,
    dateTo?: Date
  ): Promise<{ postsProcessed: number; mappingsCreated: number; errors: Array<{ postId: number; error: string }> }> => {
    const { data } = await api.post<MappingRuleApiResponse<{ postsProcessed: number; mappingsCreated: number; errors: Array<{ postId: number; error: string }> }>>(
      '/api/calendar-mapping-rules/apply-retroactive',
      {
        sourceCalendarId,
        dateFrom: dateFrom?.toISOString(),
        dateTo: dateTo?.toISOString(),
      }
    );
    return data.data;
  },

  /**
   * Get unique variation titles for autocomplete
   */
  getVariationTitles: async (search?: string): Promise<string[]> => {
    const queryParams = search ? `?search=${encodeURIComponent(search)}` : '';
    const { data } = await api.get<MappingRuleApiResponse<string[]>>(
      `/api/calendar-mapping-rules/variation-titles${queryParams}`
    );
    return data.data;
  },
};
